<template>
  <NrModal
    :show="show"
    class="request-payments-form"
    :has-secondary-button="true"
    title="Make payment"
    primary-button-text="Submit payment"
    secondary-button-text="Cancel"
    :disable-buttons="disableSubmit"
    @on-primary-click="submitPayment"
    @on-secondary-click="setActiveModal('')"
    @on-close="setActiveModal('')"
  >
    <p class="is-size-5 has-text-weight-bold section-label">
      Amount: {{ invoiceAmount }}
    </p>
    <p v-if="invoiceFeesText" class="is-italic">
      {{ invoiceFeesText }}
    </p>
    <p class="is-size-5 has-text-weight-bold section-label">Message</p>
    <!-- invoice.message is sanitized on the backend: https://github.com/nextrequest/nextrequest/blob/2ce22ce/app/views/client/invoices/_invoice.json.jbuilder#L9 -->
    <div class="content" v-html="invoice.message"></div>
    <form
      v-if="account && account.stripe_activated"
      @submit.prevent="submitPayment"
    >
      <div id="stripe-mount"></div>
      <p v-if="error" class="error has-text-danger">
        {{ error }}
      </p>
    </form>
    <form
      v-if="
        account &&
        (account.cybersource_activated || account.cybersource_test_activated)
      "
      ref="cybersourceForm"
      method="post"
      :action="cybersource.form_action"
    >
      <NrFormInput
        v-for="[field, value] in Object.entries(cybersource.params || {})"
        :key="field"
        type="hidden"
        :name="field"
        :modelValue="value"
      />
    </form>
  </NrModal>
</template>

<script lang="ts">
import NrModal from '@nextrequestco/components/src/components/NrModal'
import { mapActions, mapState } from 'vuex'
import {
  createStripePaymentIntent,
  confirmStripePayment
} from '../store/stripe_model'
import { fetchCybersourceData } from '../store/cybersource_model'
import { fetchCivicPlusPayData } from '../store/civicplus_pay_model'
import NrFormInput from '@nextrequestco/components/src/components/NrFormInput'

export default {
  name: 'RequestPaymentsModal',
  components: {
    NrModal,
    NrFormInput
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    invoice: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      stripe: null,
      stripeClientSecret: null,
      stripePaymentIntentId: null,
      stripeCard: null,
      cybersource: {},
      civicplus_pay_redirect_link: null,
      disableSubmit: true,
      error: null
    }
  },
  computed: {
    ...mapState(['account']),
    ...mapState('request_show', ['request', 'activeModal', 'requestInvoices']),
    invoiceAmount(): string {
      return (this.invoice?.total_with_fees / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
      })
    },
    invoiceFeesText(): string | null {
      const fees = this.invoice?.total_with_fees - this.invoice?.total_amount
      if (fees > 0) {
        return `* Includes ${(fees / 100).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD'
        })} convenience fee`
      }
      return null
    }
  },
  watch: {
    async show() {
      if (!this.show) return
      if (this.account.civicplus_pay_activated) {
        try {
          const response = await fetchCivicPlusPayData({
            invoice_id: this.invoice.id
          })
          this.civicplus_pay_redirect_link = response.redirect
          this.disableSubmit = false
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (_err) {
          this.$toasted.error('Failed to fetch civic plus pay data')
        }
      } else if (this.account.stripe_activated) {
        this.createStripePaymentIntent()
      } else if (
        this.account.cybersource_activated ||
        this.account.cybersource_test_activated
      ) {
        this.fetchCybersourceData()
      }
    }
  },
  methods: {
    ...mapActions('request_show', ['setActiveModal', 'fetchRequestInvoices']),
    fetchCybersourceData() {
      fetchCybersourceData({ invoice_id: this.invoice.id })
        .then((data) => {
          this.cybersource = data
          this.disableSubmit = false
        })
        .catch(() => {
          this.error = 'There was a problem loading payment information'
        })
    },
    createStripePaymentIntent(): void {
      createStripePaymentIntent({ invoice_id: this.invoice.id }).then(
        ({
          publishable_key: apiKey,
          client_secret: clientSecret,
          payment_intent_id: paymentIntentId
        }) => {
          this.stripe = (window as any).Stripe(apiKey)
          this.stripeClientSecret = clientSecret
          this.stripePaymentIntentId = paymentIntentId
          this.setupCard()
        }
      )
    },
    setupCard(): void {
      this.card = this.stripe.elements().create('card', {
        style: {
          base: {
            color: '#32325d',
            fontFamily: 'Open Sans, Arial, sans-serif',
            fontSmoothing: 'antialiased',
            fontSize: '16px',
            '::placeholder': {
              color: '#32325d'
            }
          },
          invalid: {
            fontFamily: 'Open Sans, Arial, sans-serif',
            color: '#fa755a',
            iconColor: '#fa755a'
          }
        }
      })
      this.card.mount('#stripe-mount')
      this.disableSubmit = false
      this.card.on('change', (evt) => {
        this.disableSubmit = evt.empty
        this.error = evt.error?.message
      })
    },
    submitPayment(): void {
      if (
        this.account.civicplus_pay_activated &&
        this.civicplus_pay_redirect_link
      ) {
        window.location.replace(this.civicplus_pay_redirect_link)
      } else if (this.account.cybersource_activated) {
        this.submitCybersourcePayment()
      } else if (this.account.cybersource_test_activated) {
        this.submitCybersourcePayment()
      } else if (this.account.stripe_activated) {
        this.submitStripePayment()
      }
    },
    submitCybersourcePayment(): void {
      // Submitting the form and relying on the webhook to mark it as paid
      this.$refs.cybersourceForm.submit()
    },
    submitStripePayment(): void {
      this.disableSubmit = true
      this.stripe
        .confirmCardPayment(this.stripeClientSecret, {
          payment_method: {
            card: this.card
          }
        })
        .then((res) => {
          if (res.error) {
            this.disableSubmit = false
            this.error = res.error?.message
          } else {
            this.onCompleteStripePayment()
          }
        })
    },
    onCompleteStripePayment(): void {
      confirmStripePayment({
        invoice_id: this.invoice.id,
        payment_intent_id: this.stripePaymentIntentId
      })
        .then(() => {
          this.fetchRequestInvoices(this.request.pretty_id)
          this.$toasted.success('Invoice paid')
          this.setActiveModal('')
        })
        .catch(() => {
          this.error = 'There was an error submitting your payment'
        })
    }
  }
}
</script>

<style lang="scss">
.section-label:not(:first-child) {
  margin-top: 1rem;
}
#stripe-mount {
  margin-top: 1rem;
  padding: 0.5rem;
  border-radius: 4px;
  height: 2.5em;
  border: solid 1px #6e6e87;
}
.error {
  padding-top: 0.5rem;
}
</style>
